import React, { Suspense, useContext } from "react";

import { getCardBorderStyle } from "@/app/application/theme";
import { ManualMeasurementPointSummary } from "@/features/measurements/model/types/MeasurementGroups";
import { MeasurementsHomePage } from "@/features/measurements/pages/MeasurementsHomePage";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
	Box,
	Dialog,
	DialogContent,
	IconButton,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";

import { GetLanguageValueAsString } from "@/app/application/Translate";


type Props = {
	open: boolean;
	onClose: () => void;
	title: string;
	initialSelectedMeasurement?: ManualMeasurementPointSummary | null;
};

const MeasurementsModal: React.FC<Props> = ({
	open,
	onClose,
	title,
	initialSelectedMeasurement = null,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xl"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 2.5,
					border: getCardBorderStyle(theme),
					overflow: "hidden",
					bgcolor: "background.default",
					boxShadow:
						theme.palette.mode === "dark"
							? "0 24px 48px rgba(0,0,0,0.4)"
							: "0 24px 48px rgba(0,0,0,0.12)",
					maxHeight: "90vh",
				},
			}}
			sx={{
				"& .MuiDialog-container": {
					alignItems: "center",
				},
			}}
			aria-labelledby="measurements-modal-title"
		>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: { xs: 2, sm: 2.5 },
					py: 2,
					borderBottom: `1px solid ${theme.palette.divider}`,
					bgcolor:
						theme.palette.mode === "dark"
							? alpha(theme.palette.background.paper, 0.4)
							: alpha(theme.palette.grey[500], 0.04),
					flexShrink: 0,
				}}
			>
				<Typography
					id="measurements-modal-title"
					variant="h6"
					component="h2"
					sx={{
						fontWeight: 600,
						color: "text.primary",
						letterSpacing: "-0.01em",
					}}
				>
					{title}
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Measurements.Ui.Components.Modals.MeasurementsModal.L87C17.Close", "Close")}
					onClick={onClose}
					size="medium"
					sx={{
						color: "text.secondary",
						"&:hover": {
							bgcolor: alpha(theme.palette.primary.main, 0.08),
							color: "primary.main",
						},
					}}
				>
					<CloseRoundedIcon fontSize="medium" />
				</IconButton>
			</Box>

			{/* Content */}
			<DialogContent
				sx={{
					p: 0,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
				}}
			>
				<Suspense fallback={null}>
					<MeasurementsHomePage
						initialSelectedMeasurement={initialSelectedMeasurement}
						isEmbedded
					/>
				</Suspense>
			</DialogContent>
		</Dialog>
	);
};

export default MeasurementsModal;
